import React, { useState, useCallback, useEffect } from 'react';
import type { ReasoningMetadata } from '@pineseed/spec';

export interface ReasoningStepsEditorProps {
  value?: ReasoningMetadata;
  onChange: (value: ReasoningMetadata) => void;
  disabled?: boolean;
}

export function ReasoningStepsEditor({ value, onChange, disabled = false }: ReasoningStepsEditorProps) {
  const [steps, setSteps] = useState<string[]>(value?.steps || []);
  const [conclusion, setConclusion] = useState<string>(value?.conclusion || '');

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      setSteps(value.steps || []);
      setConclusion(value.conclusion || '');
    }
  }, [value]);

  const handleStepsChange = useCallback((newSteps: string[]) => {
    setSteps(newSteps);
    onChange({ steps: newSteps, conclusion });
  }, [conclusion, onChange]);

  const handleConclusionChange = useCallback((newConclusion: string) => {
    setConclusion(newConclusion);
    onChange({ steps, conclusion: newConclusion });
  }, [steps, onChange]);

  const addStep = useCallback(() => {
    const newSteps = [...steps, ''];
    handleStepsChange(newSteps);
  }, [steps, handleStepsChange]);

  const removeStep = useCallback((index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    handleStepsChange(newSteps);
  }, [steps, handleStepsChange]);

  const updateStep = useCallback((index: number, text: string) => {
    const newSteps = [...steps];
    newSteps[index] = text;
    handleStepsChange(newSteps);
  }, [steps, handleStepsChange]);

  return (
    <div className="space-y-4">
      {/* Steps */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reasoning Steps
          </label>
          <button
            type="button"
            onClick={addStep}
            disabled={disabled}
            className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Add Step
          </button>
        </div>
        
        {steps.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            No steps added yet. Click &quot;Add Step&quot; to begin.
          </div>
        ) : (
          <div className="space-y-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 min-w-[20px]">
                  {index + 1}.
                </span>
                <textarea
                  value={step}
                  onChange={(e) => updateStep(index, e.target.value)}
                  disabled={disabled}
                  placeholder={`Step ${index + 1}`}
                  rows={2}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  disabled={disabled}
                  className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                  title="Remove step"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Conclusion */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Conclusion
        </label>
        <textarea
          value={conclusion}
          onChange={(e) => handleConclusionChange(e.target.value)}
          disabled={disabled}
          placeholder="Final conclusion or summary"
          rows={3}
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}

